/**
 * Enhanced useTransactions Hook with YNAB Support
 * 
 * Provides comprehensive transaction and budget data including:
 * - Zero-based budgeting status
 * - Budget category envelopes
 * - Spending targets
 * - Age of money metrics
 * - Traditional transaction summary
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';
import { startOfMonth, format } from 'date-fns';
import * as budgetService from '../services/budgetService';

const roundCurrency = (value) => Math.round(Number(value || 0) * 100) / 100;

export function useTransactions(userId, selectedDate = new Date()) {
  const [data, setData] = useState({
    // Traditional transaction data
    monthlyTransactions: [],
    allTransactions: [],
    carryForward: 0,
    salaryThisMonth: 0,
    expensesThisMonth: 0,

    // YNAB: Zero-based budgeting
    budget: null,
    budgetCategories: [],
    totalAllocated: 0,
    totalSpent: 0,
    unallocated: 0,
    toBeBudgeted: 0,
    isZeroBased: false,

    // YNAB: Spending targets
    categoryTargets: [],

    // YNAB: Age of money
    ageOfMoney: 0,
    ageOfMoneyStatus: 'no-data',
    daysToAgeGoal: 30,

    // Meta
    loading: true,
    error: null
  });

  const fetchData = useCallback(async () => {
    if (!userId) {
      setData(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setData(prev => ({ ...prev, loading: true, error: null }));

      const mStartStr = format(startOfMonth(selectedDate), 'yyyy-MM-dd');
      const mYearMonth = mStartStr.slice(0, 7);
      // ─────────────────────────────────────────────────────────────────
      // 1. FETCH TRADITIONAL TRANSACTION DATA
      // ─────────────────────────────────────────────────────────────────
      const { data: allTransactions = [], error: txError } = await supabase
        .from('transactions')
        .select(`*, categories(name, type, icon_slug), subcategories(name)`)
        .eq('user_id', userId)
        .order('date', { ascending: false });

      if (txError) throw txError;

      // Calculate carry forward (balance from previous months)
      const carryForward = (allTransactions || [])
        .filter(t => t.date < mStartStr)
        .reduce((sum, t) => sum + (Number(t.credit || 0) - Number(t.debit || 0)), 0);

      // Get current month transactions
      const monthlyTransactions = (allTransactions || []).filter(
        t => t.date && t.date.startsWith(mYearMonth)
      );

      // Calculate monthly totals
      const salaryThisMonth = monthlyTransactions.reduce(
        (sum, t) => sum + Number(t.credit || 0),
        0
      );
      const expensesThisMonth = monthlyTransactions.reduce(
        (sum, t) => sum + Number(t.debit || 0),
        0
      );

      // ─────────────────────────────────────────────────────────────────
      // 2. FETCH YNAB: BUDGET & ENVELOPES
      // ─────────────────────────────────────────────────────────────────
      let budget = null;
      let budgetCategories = [];
      let totalAllocated = 0;
      let totalSpent = 0;
      let unallocated = 0;
      let toBeBudgeted = 0;
      let isZeroBased = false;
      const availableFunds = roundCurrency(carryForward + salaryThisMonth);
      unallocated = availableFunds;
      toBeBudgeted = availableFunds;
      isZeroBased = toBeBudgeted === 0;

      try {
        budget = await budgetService.getBudgetForMonth(userId, selectedDate);

        if (budget) {
          // Get envelope allocations
          budgetCategories = await budgetService.getBudgetCategories(budget.id);

          totalAllocated = roundCurrency(budgetCategories.reduce(
            (sum, cat) => sum + (cat.allocated_amount || 0),
            0
          ));
          totalSpent = roundCurrency(budgetCategories.reduce(
            (sum, cat) => sum + (cat.spent_amount || 0),
            0
          ));

          toBeBudgeted = roundCurrency(availableFunds - totalAllocated);
          unallocated = toBeBudgeted;
          isZeroBased = toBeBudgeted === 0;

          // Update budget income if changed
          if (budget.total_income !== availableFunds) {
            await supabase
              .from('budgets')
              .update({ total_income: availableFunds })
              .eq('id', budget.id);
          }
        }
      } catch {
        // Continue without budget data if tables don't exist yet
      }

      // ─────────────────────────────────────────────────────────────────
      // 3. FETCH YNAB: SPENDING TARGETS
      // ─────────────────────────────────────────────────────────────────
      let categoryTargets = [];
      try {
        categoryTargets = await budgetService.getSpendingTargets(userId);
      } catch {
        // Continue without spending targets
      }

      // ─────────────────────────────────────────────────────────────────
      // 4. CALCULATE: AGE OF MONEY (YNAB Rule 4 — FIFO in-memory)
      //
      // We sort all transactions by date, then walk them chronologically:
      //   • Income entries fill an in-order queue.
      //   • Each expense consumes the oldest available income first.
      //   • For expenses that fall in the selected month we accumulate
      //     a weighted age: Σ(consumed_amount × age_in_days).
      //   • Age of money = Σ(weighted_age) / Σ(consumed_amount this month)
      //
      // This works entirely from dates already in memory — no stored
      // money_age_days field required.
      // ─────────────────────────────────────────────────────────────────
      let ageOfMoney = 0;
      let ageOfMoneyStatus = 'no-data';
      let daysToAgeGoal = 30;

      try {
        const sorted = [...(allTransactions || [])].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        // Income FIFO queue: each slot tracks how much is still available
        const incomeQueue = sorted
          .filter(t => Number(t.credit || 0) > 0)
          .map(t => ({ date: new Date(t.date), available: Number(t.credit) }));

        const expenses = sorted.filter(t => Number(t.debit || 0) > 0);

        let queueIdx = 0;
        let totalWeightedAge = 0;
        let totalConsumed = 0;

        for (const expense of expenses) {
          const expDate = new Date(expense.date);
          const isCurrentMonth = expense.date.startsWith(mYearMonth);
          let remaining = Number(expense.debit);

          while (remaining > 0 && queueIdx < incomeQueue.length) {
            const inc = incomeQueue[queueIdx];
            // Can't use income received after this expense
            if (inc.date > expDate) break;

            const consume = Math.min(remaining, inc.available);
            if (isCurrentMonth) {
              const ageDays = Math.max(
                0,
                Math.round((expDate - inc.date) / 86400000)
              );
              totalWeightedAge += ageDays * consume;
              totalConsumed += consume;
            }

            inc.available -= consume;
            remaining -= consume;
            if (inc.available <= 0) queueIdx++;
          }
        }

        if (totalConsumed > 0) {
          ageOfMoney = Math.round(totalWeightedAge / totalConsumed);
          ageOfMoneyStatus = ageOfMoney >= 30 ? 'on-track' : 'behind';
          daysToAgeGoal = Math.max(0, 30 - ageOfMoney);
        }
      } catch {
        // Age of money calc is non-critical
      }

      // ─────────────────────────────────────────────────────────────────
      // 5. UPDATE STATE
      // ─────────────────────────────────────────────────────────────────
      setData({
        monthlyTransactions,
        allTransactions,
        carryForward,
        salaryThisMonth,
        expensesThisMonth,
        budget,
        budgetCategories,
        totalAllocated,
        totalSpent,
        unallocated,
        toBeBudgeted,
        isZeroBased,
        categoryTargets,
        ageOfMoney,
        ageOfMoneyStatus,
        daysToAgeGoal,
        loading: false,
        error: null
      });
    } catch (err) {
      setData(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to fetch transaction data'
      }));
    }
  }, [userId, selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─────────────────────────────────────────────────────────────────
  // PUBLIC API
  // ─────────────────────────────────────────────────────────────────
  return {
    // Traditional data (existing API)
    monthlyTransactions: data.monthlyTransactions,
    allTransactions: data.allTransactions,
    carryForward: data.carryForward,
    salaryThisMonth: data.salaryThisMonth,
    expensesThisMonth: data.expensesThisMonth,

    // YNAB data
    budget: data.budget,
    budgetCategories: data.budgetCategories,
    totalAllocated: data.totalAllocated,
    totalSpent: data.totalSpent,
    unallocated: data.unallocated,
    toBeBudgeted: data.toBeBudgeted,
    isZeroBased: data.isZeroBased,
    categoryTargets: data.categoryTargets,
    ageOfMoney: data.ageOfMoney,
    ageOfMoneyStatus: data.ageOfMoneyStatus,
    daysToAgeGoal: data.daysToAgeGoal,

    // Status
    loading: data.loading,
    error: data.error,
    refresh: fetchData
  };
}
